import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { recordDriverPayment, recordDriverPayout } from '../api/supabase';

interface DriverPaymentModalProps {
  isVisible: boolean;
  onClose: () => void;
  driverBalance: {
    driver_id: string;
    drivers: { name: string };
    current_balance: number;
  };
  onPaymentSuccess: () => void;
}

type PaymentType = 'debt_payment' | 'driver_payout';
type PaymentMethod = 'cash' | 'bank_transfer' | 'check' | 'deposit';

const DriverPaymentModal: React.FC<DriverPaymentModalProps> = ({
  isVisible,
  onClose,
  driverBalance,
  onPaymentSuccess,
}) => {
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    accountNumber: '',
    iban: '',
  });
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  // Determine payment type based on balance
  const isNegativeBalance = driverBalance.current_balance < 0;
  const isPositiveBalance = driverBalance.current_balance > 0;
  const paymentType: PaymentType = isNegativeBalance ? 'debt_payment' : 'driver_payout';

  // Calculate available amounts
  const maxAmount = isNegativeBalance 
    ? Math.abs(driverBalance.current_balance) 
    : driverBalance.current_balance;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const validateAmount = () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return 'Please enter a valid amount greater than zero';
    }
    if (numAmount > maxAmount) {
      return `Amount cannot exceed ${formatCurrency(maxAmount)}`;
    }
    return null;
  };

  const handlePayment = async () => {
    const validation = validateAmount();
    if (validation) {
      Alert.alert('Invalid Amount', validation);
      return;
    }

    const numAmount = parseFloat(amount);

    // Show confirmation
    const title = paymentType === 'debt_payment' 
      ? 'Confirm Payment Receipt'
      : 'Confirm Driver Payout';
      
    const message = paymentType === 'debt_payment'
      ? `Record payment of ${formatCurrency(numAmount)} received from ${driverBalance.drivers.name}?`
      : `Record payout of ${formatCurrency(numAmount)} to ${driverBalance.drivers.name}?`;

    Alert.alert(
      title,
      message,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Confirm', onPress: processPayment },
      ]
    );
  };

  const processPayment = async () => {
    try {
      setLoading(true);
      const numAmount = parseFloat(amount);

      let result;
      if (paymentType === 'debt_payment') {
        result = await recordDriverPayment(
          driverBalance.driver_id,
          numAmount,
          paymentMethod as 'cash' | 'bank_transfer' | 'deposit',
          referenceNumber || undefined
        );
      } else {
        result = await recordDriverPayout(
          driverBalance.driver_id,
          numAmount,
          paymentMethod as 'bank_transfer' | 'cash' | 'check',
          referenceNumber || undefined,
          showBankDetails ? bankDetails : undefined
        );
      }

      if (result.success) {
        const successMessage = paymentType === 'debt_payment'
          ? `Payment of ${formatCurrency(numAmount)} recorded successfully!\n\nNew balance: ${formatCurrency(result.newBalance)}`
          : `Payout of ${formatCurrency(numAmount)} recorded successfully!\n\nNew balance: ${formatCurrency(result.newBalance)}`;

        Alert.alert('Success', successMessage, [
          { text: 'OK', onPress: () => {
            onPaymentSuccess();
            onClose();
          }}
        ]);
      }
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAmount('');
    setReferenceNumber('');
    setBankDetails({ bankName: '', accountNumber: '', iban: '' });
    setShowBankDetails(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getPaymentMethods = (): PaymentMethod[] => {
    if (paymentType === 'debt_payment') {
      return ['cash', 'bank_transfer', 'deposit'];
    } else {
      return ['bank_transfer', 'cash', 'check'];
    }
  };

  if (!isVisible) return null;

  return (
    <View style={styles.overlay}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>
              {paymentType === 'debt_payment' ? 'Record Payment' : 'Record Payout'}
            </Text>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {/* Driver Info */}
            <View style={styles.driverInfo}>
              <Text style={styles.driverName}>{driverBalance.drivers.name}</Text>
              <Text style={[
                styles.currentBalance, 
                { color: driverBalance.current_balance >= 0 ? '#27AE60' : '#E74C3C' }
              ]}>
                Current Balance: {formatCurrency(driverBalance.current_balance)}
              </Text>
              {paymentType === 'debt_payment' && (
                <Text style={styles.debtInfo}>
                  Outstanding Debt: {formatCurrency(Math.abs(driverBalance.current_balance))}
                </Text>
              )}
              {paymentType === 'driver_payout' && (
                <Text style={styles.creditInfo}>
                  Available Credit: {formatCurrency(driverBalance.current_balance)}
                </Text>
              )}
            </View>

            {/* Amount Input */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>
                {paymentType === 'debt_payment' ? 'Payment Amount' : 'Payout Amount'}
              </Text>
              <TextInput
                style={styles.amountInput}
                value={amount}
                onChangeText={setAmount}
                placeholder={`0.00 (Max: ${formatCurrency(maxAmount)})`}
                keyboardType="numeric"
                returnKeyType="done"
              />
            </View>

            {/* Payment Method */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Payment Method</Text>
              <View style={styles.methodContainer}>
                {getPaymentMethods().map((method) => (
                  <TouchableOpacity
                    key={method}
                    style={[
                      styles.methodButton,
                      paymentMethod === method && styles.methodButtonActive
                    ]}
                    onPress={() => {
                      setPaymentMethod(method);
                      setShowBankDetails(method === 'bank_transfer');
                    }}
                  >
                    <Text style={[
                      styles.methodText,
                      paymentMethod === method && styles.methodTextActive
                    ]}>
                      {method.replace('_', ' ').toUpperCase()}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Reference Number */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Reference Number (Optional)</Text>
              <TextInput
                style={styles.input}
                value={referenceNumber}
                onChangeText={setReferenceNumber}
                placeholder="Enter reference number"
                returnKeyType="done"
              />
            </View>

            {/* Bank Details for Bank Transfer */}
            {paymentMethod === 'bank_transfer' && paymentType === 'driver_payout' && (
              <View style={styles.inputGroup}>
                <View style={styles.switchContainer}>
                  <Text style={styles.label}>Include Bank Details</Text>
                  <Switch
                    value={showBankDetails}
                    onValueChange={setShowBankDetails}
                    trackColor={{ false: '#E5E5E5', true: '#4CAF50' }}
                    thumbColor={showBankDetails ? '#FFFFFF' : '#FFFFFF'}
                  />
                </View>

                {showBankDetails && (
                  <View style={styles.bankDetailsContainer}>
                    <TextInput
                      style={styles.input}
                      value={bankDetails.bankName}
                      onChangeText={(text) => setBankDetails(prev => ({ ...prev, bankName: text }))}
                      placeholder="Bank Name"
                      returnKeyType="next"
                    />
                    <TextInput
                      style={styles.input}
                      value={bankDetails.accountNumber}
                      onChangeText={(text) => setBankDetails(prev => ({ ...prev, accountNumber: text }))}
                      placeholder="Account Number"
                      returnKeyType="next"
                    />
                    <TextInput
                      style={styles.input}
                      value={bankDetails.iban}
                      onChangeText={(text) => setBankDetails(prev => ({ ...prev, iban: text }))}
                      placeholder="IBAN (Optional)"
                      returnKeyType="done"
                    />
                  </View>
                )}
              </View>
            )}

            {/* Preview */}
            {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
              <View style={styles.preview}>
                <Text style={styles.previewTitle}>Transaction Preview</Text>
                <View style={styles.previewRow}>
                  <Text style={styles.previewLabel}>Current Balance:</Text>
                  <Text style={styles.previewValue}>
                    {formatCurrency(driverBalance.current_balance)}
                  </Text>
                </View>
                <View style={styles.previewRow}>
                  <Text style={styles.previewLabel}>
                    {paymentType === 'debt_payment' ? 'Payment:' : 'Payout:'}
                  </Text>
                  <Text style={[
                    styles.previewValue,
                    { color: paymentType === 'debt_payment' ? '#27AE60' : '#E74C3C' }
                  ]}>
                    {paymentType === 'debt_payment' ? '+' : '-'}{formatCurrency(parseFloat(amount))}
                  </Text>
                </View>
                <View style={[styles.previewRow, styles.previewTotal]}>
                  <Text style={styles.previewLabel}>New Balance:</Text>
                  <Text style={[
                    styles.previewValue,
                    styles.previewValueBold,
                    { 
                      color: (paymentType === 'debt_payment' 
                        ? driverBalance.current_balance + parseFloat(amount)
                        : driverBalance.current_balance - parseFloat(amount)) >= 0 
                        ? '#27AE60' : '#E74C3C' 
                    }
                  ]}>
                    {formatCurrency(
                      paymentType === 'debt_payment'
                        ? driverBalance.current_balance + parseFloat(amount)
                        : driverBalance.current_balance - parseFloat(amount)
                    )}
                  </Text>
                </View>
              </View>
            )}
          </ScrollView>

          {/* Action Buttons */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={handleClose}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.button, 
                styles.submitButton,
                loading && styles.submitButtonDisabled
              ]}
              onPress={handlePayment}
              disabled={loading || !amount || validateAmount() !== null}
            >
              <Text style={styles.submitButtonText}>
                {loading 
                  ? 'Processing...' 
                  : paymentType === 'debt_payment' 
                    ? 'Record Payment'
                    : 'Record Payout'
                }
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  container: {
    width: '90%',
    maxHeight: '90%',
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    maxHeight: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#2C3E50',
  },
  closeButton: {
    padding: 4,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  driverInfo: {
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  driverName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 8,
  },
  currentBalance: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },
  debtInfo: {
    fontSize: 14,
    color: '#E74C3C',
    fontWeight: '500',
  },
  creditInfo: {
    fontSize: 14,
    color: '#27AE60',
    fontWeight: '500',
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2C3E50',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#FFFFFF',
  },
  amountInput: {
    borderWidth: 2,
    borderColor: '#4CAF50',
    borderRadius: 8,
    padding: 16,
    fontSize: 18,
    backgroundColor: '#FFFFFF',
    fontWeight: '600',
  },
  methodContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  methodButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#DDD',
    backgroundColor: '#FFFFFF',
  },
  methodButtonActive: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  methodText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  methodTextActive: {
    color: '#FFFFFF',
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  bankDetailsContainer: {
    gap: 12,
  },
  preview: {
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 8,
    marginTop: 10,
  },
  previewTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 12,
  },
  previewRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  previewTotal: {
    borderTopWidth: 1,
    borderTopColor: '#DDD',
    paddingTop: 8,
    marginTop: 8,
  },
  previewLabel: {
    fontSize: 14,
    color: '#666',
  },
  previewValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2C3E50',
  },
  previewValueBold: {
    fontWeight: '700',
    fontSize: 15,
  },
  footer: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#95A5A6',
  },
  cancelButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  submitButton: {
    backgroundColor: '#4CAF50',
  },
  submitButtonDisabled: {
    backgroundColor: '#BDC3C7',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default DriverPaymentModal;
